import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { KilnService } from 'src/app/services/kiln.service';
import { MaintenanceNoteService } from 'src/app/services/maintenance_notes.service';
import { ToggleService } from 'src/app/services/toggle.service';
import { UserService } from 'src/app/services/user.service';
import { AlertController, PickerController, Platform } from '@ionic/angular';
import moment from 'moment';

@Component({
  selector: 'app-maintenance-tab',
  templateUrl: './maintenance-tab.component.html',
  styleUrls: ['./maintenance-tab.component.scss'],
})
export class MaintenanceTabComponent implements OnInit {
  @Input() kilnId;
  @Input() viewingKilnlinkFreeKilns;
  public is_editing = undefined;
  public maintenanceNoteForm: UntypedFormGroup;
  public maintenance_notes = [];
  public small_screen = false;
  public selectedNote;
  public isCreatingNew = false;
  public isEditing = false;
  public retrievingNotes = false;
  public maintenanceNotesEnabled = false;
  public userRole = 'basic'; // Default to basic, will be set from service

  constructor(
    public kilnService: KilnService,
    public maintenanceNoteService: MaintenanceNoteService,
    public toggleService: ToggleService,
    public userService: UserService,
    public alertController: AlertController,
    public platform: Platform,
    private pickerController: PickerController
  ) {
    let defaultValidation = () => { return new UntypedFormControl('', [Validators.required]);}
    let is_device = this.platform ? (this.platform.is('mobile') || this.platform.is('tablet') || this.platform.is('phablet') || this.platform.is('mobileweb')) : false;
    this.small_screen = this.platform.width() < 875 || is_device;

    this.retrievingNotes = this.small_screen;
    this.initializeForm();
  }

  initializeForm() {
    let defaultValidation = () => { return new UntypedFormControl('', [Validators.required]);}
    
    const formControls: any = {
      date: defaultValidation(),
      technician: defaultValidation(),
      type: defaultValidation(),
      notes: defaultValidation(),
      note_id: defaultValidation(),
      complete: new UntypedFormControl(true) // Default to true
    };

    // Only add status field if maintenance notes toggle is enabled
    if (this.maintenanceNotesEnabled) {
      formControls.status = new UntypedFormControl('requested'); // Default to requested
    }

    this.maintenanceNoteForm = new UntypedFormGroup(formControls);
  }

  ngOnInit() {
    this.isEditing = false;
    this.isCreatingNew = false;
    this.retrievingNotes = true;
    this.getUserRole();
    this.checkMaintenanceNotesToggle();
    if(!this.viewingKilnlinkFreeKilns) {      
      this.maintenanceNoteService.list(this.kilnId)
          .then((response) => { this.onListSuccess(response) })
          .catch((error) => {
            this.retrievingNotes = false;
            this.showError("Could not retrieve maintenance notes at this time.")
          })
    }
  }

  getUserRole() {
    // Get user role from the user service
    const loginData = this.userService.getLoginData();
    if (loginData && loginData.role) {
      this.userRole = loginData.role;
    } else {
      // Fallback to basic if no role found
      this.userRole = 'basic';
    }
  }

  async checkMaintenanceNotesToggle() {
    try {
      this.maintenanceNotesEnabled = this.toggleService.isToggleEnabled('maintenanceNotes');
      // Reinitialize form with or without status field based on toggle
      this.initializeForm();
    } catch (error) {
      console.error('Error checking maintenance notes toggle:', error);
      this.maintenanceNotesEnabled = false;
      this.initializeForm();
    }
  }

  ngOnChanges() {
    this.isEditing = false;
    this.isCreatingNew = false;
  }

  public maintenance_types = [
    {value:"vacuum_interior", option_text: "Vacuum interior"},
    {value:"check_thermocouple_using_cones", option_text: "Check Thermocouple using cones"},
    {value:"check_all_hardware", option_text: "Check all hardware around kiln, lid brace, screws, handles, etc."},
    {value:"review_diagnostics", option_text: "Review diagnostic data"},
    {value:"check_envirovent", option_text: "Check EnviroVent for proper draw through lid hole"},
    {value:"replace_all_elements", option_text: "Replace all elements"},
    {value:"replace_one_element", option_text: "Replace one element"},
    {value:"replace_relays", option_text: "Replace relays"},
    {value:"replace_wiring_harness", option_text: "Replace wiring harness"},
    {value:"replace_bricks", option_text: "Replace bricks (note location)"},
    {value:"replace_reverse_lid", option_text: "Replace / reverse lid"},
    {value:"replace_reverse_bottom_slab", option_text: "Replace or reverse bottom slab"},
    {value:"environlink", option_text: "Install EnviroLink"},
    {value:"replace_thermocouple", option_text: "Replace thermocouple"},
    {value:"repair", option_text: "Repair"},
    {value:"other", option_text: "Other"}
  ]

  public getMaintenanceTypes() {
    if (this.maintenanceNotesEnabled) {
      return this.maintenance_types;
    } else {
      // Return only the original types without repair
      return this.maintenance_types.filter(type => type.value !== 'repair');
    }
  }

  public maintenance_statuses = [
    {value:"requested", option_text: "Requested"},
    {value:"assigned", option_text: "Assigned"},
    {value:"in_progress", option_text: "In Progress"},
    {value:"completed", option_text: "Completed"},
    {value:"cancelled", option_text: "Cancelled"},
    {value:"rejected", option_text: "Rejected"}
  ]

  public getFriendlyType(val) {
    let maintenance_type = this.maintenance_types.find((t) => { return t.value === val; })
    return maintenance_type ? maintenance_type.option_text : "N/A";
  }

  public getFriendlyStatus(val) {
    let status = this.maintenance_statuses.find((s) => { return s.value === val; })
    return status ? status.option_text : "N/A";
  }

  public getStatusColor(status) {
    switch(status) {
      case 'requested': return 'warning';
      case 'assigned': return 'primary';
      case 'in_progress': return 'secondary';
      case 'completed': return 'success';
      case 'cancelled': return 'medium';
      case 'rejected': return 'danger';
      default: return 'medium';
    }
  }

  public isBasicOrReadonlyUser() {
    // Basic/readonly users are those who can only request maintenance
    return this.userRole === 'user' || this.userRole === 'basic' || this.userRole === 'readonly';
  }

  public canEditNotes() {
    // Admin and tech users can edit notes
    return this.userRole === 'super-admin' || this.userRole === 'tech' || this.userRole === 'admin';
  }

  public canSetStatus() {
    // Admin and tech users can set status
    return this.userRole === 'super-admin' || this.userRole === 'tech' || this.userRole === 'admin';
  }

  public canMarkComplete() {
    // Admin and tech users can mark complete
    return this.userRole === 'super-admin' || this.userRole === 'tech' || this.userRole === 'admin';
  }

  public requestMaintenance() {
    this.isCreatingNew = true;
    this.isEditing = false;
    this.selectedNote = undefined;
    this.clearForm();
    
    // For basic/readonly users, set future date and restricted fields
    if (this.isBasicOrReadonlyUser()) {
      const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
      this.maintenanceNoteForm.patchValue({
        date: tomorrow,
        status: 'requested',
        complete: new UntypedFormControl(false)
      });
    }
  }

  public createNewNote() {
    this.isCreatingNew = true;
    this.isEditing = false;
    this.selectedNote = undefined;
    this.clearForm();
    
    // For basic/readonly users, set future date and restricted fields
    if (this.isBasicOrReadonlyUser()) {
      const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
      this.maintenanceNoteForm.patchValue({
        date: tomorrow,
        status: 'requested',
        complete: new UntypedFormControl(false)
      });
    }
  }

  public selectNote(note) {
    this.selectedNote = note;
    this.isCreatingNew = false;
    this.isEditing = false;
  }

  public editNote(note) {
    this.isEditing = true;
    this.isCreatingNew = false;
    this.loadToMaintenanceForm(note);
  }

  public cancelForm() {
    this.isEditing = false;
    this.isCreatingNew = false;
    this.clearForm();
  }

  onSubmit(value) {
    // Prepare the data to send, conditionally including status
    const submitData: any = {
      technician: value.technician,
      type: value.type,
      date: value.date,
      notes: value.notes,
      complete: value.complete ? true : false
    };

    // For basic/readonly users, enforce restrictions
    if (this.isBasicOrReadonlyUser()) {
      // Force future date
      const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
      submitData.date = tomorrow;
      
      // Force requested status and incomplete
      submitData.status = 'requested';
      submitData.complete = false;
    } else {
      // Only include status if maintenance notes toggle is enabled and user can set status
      if (this.maintenanceNotesEnabled && value.status && this.canSetStatus()) {
        submitData.status = value.status;
      }
    }

    if(this.isEditing) {
      submitData.note_id = value.note_id;
      this.maintenanceNoteService.update(this.kilnId, submitData)
          .then((response) => { this.onUpdateSuccess(response) })
          .catch((error) => { this.onUpdateError(error) })
    } else {
      this.maintenanceNoteService.create(this.kilnId, submitData)
          .then(() => {
            this.onCreateSuccess(submitData);
          })
          .catch((error) => { this.onCreateError(error) })
    }
  }

  onCreateSuccess(data) {
    this.maintenanceNoteService.list(this.kilnId)
        .then((response) => { this.onListSuccess(response) })
        .catch((error) => this.showError("Could not retrieve maintenance notes at this time."))
    this.isCreatingNew = false;
    this.clearForm();
  }

  onUpdateSuccess(data) {
    this.maintenanceNoteService.list(this.kilnId)
        .then((response) => { 
          this.onListSuccess(response);
          // Update the selected note with the new data
          if (this.selectedNote) {
            const updatedNote = this.maintenance_notes.find(note => note.id === this.selectedNote.id);
            if (updatedNote) {
              this.selectedNote = updatedNote;
            }
          }
        })
        .catch((error) => this.showError("Could not retrieve maintenance notes at this time."))
    this.isEditing = false;
    this.clearForm();
  }

  onListSuccess(data) {
    this.retrievingNotes = false;
    this.maintenance_notes = data.sort((a, b) => {
      let device1_greater = moment(a.note_date).isAfter(moment(b.note_date));
      let device2_greater = moment(a.note_date).isAfter(moment(b.note_date));
      if(device1_greater) { return -1 }
      else if(device2_greater) { return 1 }
      else return 0;
    });
    if(!this.selectedNote && this.maintenance_notes.length > 0) {
      this.selectedNote = this.maintenance_notes[0]
    }
  }

  deleteNoteSuccess(data) {
    this.maintenanceNoteService.list(this.kilnId)
        .then((response) => { this.onListSuccess(response) })
        .catch((error) => this.showError("Could not retrieve maintenance notes at this time."))
  }

  formatErrorMessages(messages) {
    return messages.map((msg) => {
      msg = msg.replace('\"', '');
      msg = msg.replace('"', '');
      msg = msg.replace('_', ' ');
      msg = `${msg.charAt(0).toUpperCase()}${msg.slice(1)}.`;
      return msg;
    }).join(" ");
  }

  onCreateError(error) {
    this.showError(error.error ? `${error.error.message} - ${this.formatErrorMessages(error.error.msgs)}` : "Could not create maintenance note.");
  }

  onUpdateError(error) {
    this.showError(error.error ? `${error.error.message} - ${this.formatErrorMessages(error.error.msgs)}` : "Could not update maintenance note.");
  }

  async showError(message) {
    let alert = await this.alertController.create({
       message: message,
       buttons: [{
         text:'Ok',
         role: 'cancel',
       }]
     });

   await alert.present();
  }

  clearForm() {
    this.maintenanceNoteForm.reset();
    this.isEditing = false;
  }

  loadToMaintenanceForm(note) {
    if(note) {
      this.isEditing = true;
      let maintenance_type = this.maintenance_types.find((t) => { return t.value === note.type; })

      const formControls: any = {
        date: new UntypedFormControl(note.note_date ? moment(note.note_date).format("YYYY-MM-DD") : '', [Validators.required]),
        technician: new UntypedFormControl(note.technician, [Validators.required]),
        type: new UntypedFormControl(note.type, [Validators.required]),
        notes: new UntypedFormControl(note.notes, [Validators.required]),
        note_id: new UntypedFormControl(note.id),
        complete: new UntypedFormControl(note.complete ? true : false) // Default to true if not explicitly false
      };

      // Only add status field if maintenance notes toggle is enabled
      if (this.maintenanceNotesEnabled) {
        formControls.status = new UntypedFormControl(note.status || 'requested'); // Default to requested if not set
      }

      this.maintenanceNoteForm = new UntypedFormGroup(formControls);
    }    
  }

  async deleteNote(note) {
    let alert = await this.alertController.create({
       message: "Are you sure you want to delete this note?",
       buttons: [{
         text:'Cancel',
         role: 'cancel',
       }, {
         text: "Yes",
         handler: () => {
           this.maintenanceNoteService.delete(this.kilnId, note.id)
               .then((response) => { this.deleteNoteSuccess(response) })
               .catch((error) => this.showError("Note could not be deleted"))
         }
       }]
     });

   await alert.present();
  }

  public pickerButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
    },
    {
      text: 'Confirm',
      handler: (selection) => {
        this.selectedNote = selection.notes.value;
      },
    },
  ];

  async showPicker() {
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'notes',
        options: this.maintenance_notes.map((note) => { return {text: moment(note.note_date).utc().format('MM/DD/YY').toString(), value: note}})
      }],
      buttons: this.pickerButtons
    });

    await picker.present();
  }
}
